import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { Container } from '@pulse/ui';
import type { ReactNode } from 'react';

interface PageContainerProps {
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false | number;
  /**
   * Optional per-page override, shallow-merged over the default padding/spacing below — for
   * the rare page that needs a different horizontal footprint (e.g. a wide table). Since this
   * is a plain object merge (not an sx array), a responsive key like `px` must be restated in
   * full (all breakpoints you want), not just the one breakpoint being changed. Omit to get the
   * standard spacing every other PageContainer consumer already relies on.
   */
  sx?: SystemStyleObject<Theme>;
}

// Standard wrapper for authenticated app pages (list/detail/form) — gives every domain
// feature the same responsive horizontal padding and max-width without each page
// re-deriving its own breakpoint values. Not for full-bleed screens like login.
//
// `maxWidth` also accepts a raw pixel number for a page that needs something between two of
// MUI's breakpoint steps (e.g. a wide data table that overflows `lg` but has room to spare
// under `xl`) — passed through as an `sx` override rather than the `Container`'s own
// breakpoint-keyed `maxWidth` prop, which only understands the fixed 'sm'/'md'/'lg'/'xl' steps.
export function PageContainer({ children, maxWidth = 'xl', sx }: PageContainerProps) {
  if (typeof maxWidth === 'number') {
    return (
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          maxWidth,
        }}
      >
        {children}
      </Container>
    );
  }
  return (
    <Container
      maxWidth={maxWidth}
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        ...sx,
      }}
    >
      {children}
    </Container>
  );
}
